import {h, Fragment} from "preact"
import { useEffect, useState, useRef } from "preact/hooks";

export default function CustomDropdown({ value, onChange, className, options, label }) {
    const [ open, setOpen ] = useState(false);
    const [ focusIndex, setFocusIndex ] = useState(options.findIndex(o => o.value === value) ?? 0);
    const listRef = useRef(null);
    const toggleRef = useRef(null);

    useEffect(() => { // focus when tabbing / keyboard arrow
        if (open && listRef.current && listRef.current.children.length) {
            const selectedElement = listRef.current.children[focusIndex];
            if (selectedElement) {
                setTimeout(() => {
                    selectedElement.focus();
                }, 50)
            }
        }
    }, [ open, focusIndex ]);

    useEffect(() => { // keyboard controls
        const handleKeydown = (event) => {
            switch(event.key) {
                case "Escape":
                case "Tab" :
                    setOpen(false);
                    break;
                case "ArrowUp":
                    setFocusIndex(prevFocusIndex => (prevFocusIndex > 0) ? prevFocusIndex - 1 : prevFocusIndex)
                    break;
                case "ArrowDown":
                    setFocusIndex(prevFocusIndex => (prevFocusIndex < (options.length - 1)) ? prevFocusIndex + 1 : prevFocusIndex)
                    break;
                case "Enter":
                    setOpen(false);
                    break;
                default:
                    break;
            }
        }
        if(open && listRef.current) {
            window.document.addEventListener('keydown', handleKeydown)
            return () => window.document.removeEventListener('keydown', handleKeydown)
        }
    }, [ open, focusIndex ])

    useEffect(() => { // close dropdown from outside clicks
        const handleClick = (event) => {
            if(
                !listRef.current.contains(event.target) && 
                !toggleRef.current.contains(event.target)
            ) {
                setOpen(false);
            };
        };

        if(open && listRef?.current && toggleRef?.current) {
            window.document.addEventListener('click', handleClick);
            return () => window.document.removeEventListener('click', handleClick);
        };
    }, [ open ]);

    const toggleSort = () => {
        setOpen(prevOpen => !prevOpen);
    };

    return (
        <div class="relative ns-dropdown">

            <button 
                type="button"
                class="button--pill flex items-center"
                aria-haspopup="listbox"
                aria-controls={`ns-dropdown-content-${label}`}
                aria-labelledby={`ns-header-${label}`}
                aria-expanded={open}
                onClick={toggleSort}
                ref={toggleRef}
            >
                <h4 id={`ns-header-${label}`} class="ns-dropdown-heading">Sort</h4>
                <span>
                    <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4.60207 6.75076C4.21535 6.94998 4.04348 7.3992 4.19582 7.80545C4.25442 7.9617 4.6177 8.34451 6.82863 10.5594C8.27785 12.0125 9.46926 13.1765 9.56692 13.2351C9.78567 13.3679 10.1685 13.3797 10.3872 13.2664C10.4614 13.2273 11.6802 12.0437 13.0904 10.6297C14.9771 8.74686 15.6763 8.0242 15.7232 7.91092C15.7583 7.82498 15.7896 7.65701 15.7857 7.53592C15.7857 7.06717 15.4458 6.71951 14.981 6.70779C14.563 6.69998 14.6489 6.62967 12.231 9.03982L9.9966 11.2703L7.75832 9.03592C6.0552 7.32889 5.48098 6.78201 5.34817 6.72342C5.09817 6.61014 4.85598 6.61795 4.60207 6.75076Z" fill="#2A2829"></path>
                    </svg>
                </span>
            </button>
            
            <div
                class="ns-custom-dropdown"
                id={`ns-dropdown-content-${label}`}
                aria-hidden={!open}
            >
                <fieldset
                    onChange={e => {
                        e.preventDefault();
                        onChange(e);
                        setFocusIndex(options.findIndex(o => o.value === value));
                        setOpen(false);
                    }}
                >
                    <legend class="sr-only visually-hidden">Sort by</legend>
                    <ul class="flex flex-col px-4 py-2" role="listbox" ref={listRef}>
                        {options.map((option, i) => (
                            <li
                                key={option.value}
                                class="flex flex-nowrap items-center gap-1"
                                role="option"
                            >
                                <input
                                    class="cursor-pointer"
                                    type="radio"
                                    id={`ns-${label}-${option.text.toLowerCase().replaceAll(' ', '-')}`}
                                    value={option.value}
                                    checked={value === option.value}
                                    name={`dropdown-option-${label}`}
                                />
                                <label 
                                    htmlFor={`ns-${label}-${option.text.toLowerCase().replaceAll(' ', '-')}`}
                                    class="cursor-pointer"
                                >
                                    {option.text}
                                </label>
                            </li>
                        ))}
                    </ul>
                </fieldset>
            </div>
        </div>
    )
}